import React, { FC, ReactNode } from "react";

import { BaseComponentProps, useThemeColors } from "@keepeek/refront-components";
import { KTheme } from "@keepeek/refront-customers";
import { alpha, Box, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";

import { Element } from "../../../../models/configuration/pages/element";
import { useConfiguration } from "../../../../providers/config/hooks";
import { CONFIGURATION_SECTION_PATH } from "../../adminMenu";
import { toolBarHeight } from "../../constants";

const PREFIX = "MediaDetailFixedLayout";

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  navigation: `${PREFIX}-navigation`,
  viewer: `${PREFIX}-viewer`,
  header: `${PREFIX}-header`,
  kTemplates: `${PREFIX}-kTemplates`,
  fullHeight: `${PREFIX}-fullHeight`,
};

const StyledMediaDetailFixedLayout = styled(Box)(({ theme }: { theme: KTheme }) => ({
  [`&.${classes.root}`]: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 100%)",
    gridTemplateAreas: '"navigation" "viewer" "actions" "header" "kTemplate"',
    [theme.breakpoints.up("lg")]: {
      gridTemplateColumns: "minmax(0, 65%) minmax(0, 35%)",
      gridTemplateRows: "min-content min-content 1fr",
      gridTemplateAreas: '"navigation actions" "viewer header" "viewer kTemplate"',
    },
    alignItems: "stretch",
  },

  [`& .${classes.actions}`]: {
    gridArea: "actions",
  },

  [`& .${classes.navigation}`]: {
    gridArea: "navigation",
  },

  [`& .${classes.viewer}`]: {
    gridArea: "viewer",
    backgroundColor: theme.palette.kViewer?.background.dark || theme.palette.common.black,
    overflow: "auto",
    [theme.breakpoints.down("lg")]: {
      height: "50vh",
    },
  },

  [`& .${classes.header}`]: {
    gridArea: "header",
  },

  [`& .${classes.kTemplates}`]: {
    gridArea: "kTemplate",
    overflow: "auto",
    // keep tabs displayed on top of screen when scrolling
    ":has(.KTabsWidget)": {
      [theme.breakpoints.down("lg")]: {
        overflow: "visible",
      },
    },
  },

  [`&.${classes.fullHeight}`]: {
    [theme.breakpoints.up("lg")]: {
      height: `calc(100vh - ${toolBarHeight})`,
    },
  },
}));

export type MediaDetailFixedLayoutProps = {
  viewer: ReactNode;
  navigation: ReactNode;
  actions: ReactNode;
  title: ReactNode;
  kTemplates: ReactNode;
} & BaseComponentProps;

const MediaDetailFixedLayout: FC<React.PropsWithChildren<MediaDetailFixedLayoutProps>> =
  function MediaDetailFixedLayout({ viewer, kTemplates, navigation, actions, title, className }) {
    const pageElementConfig = useConfiguration<Element>(CONFIGURATION_SECTION_PATH.PAGES_ELEMENT);
    const theme = useTheme<KTheme>();
    const { convertColor } = useThemeColors();
    const navigationBackgroundColor =
      convertColor(pageElementConfig?.background?.main) ||
      theme.palette.kElement?.background?.main ||
      theme.palette.grey[100];

    const actionBackgroundColor =
      convertColor(pageElementConfig?.background?.dark) || alpha(navigationBackgroundColor, 0.5);

    return (
      <StyledMediaDetailFixedLayout
        className={clsx("MediaDetailFixedLayout", classes.fullHeight, classes.root, className)}
      >
        <Box
          className={classes.navigation}
          sx={{
            backgroundColor: navigationBackgroundColor,
          }}
        >
          {navigation}
        </Box>
        <Box className={classes.viewer}>{viewer}</Box>
        <Box
          className={classes.actions}
          sx={{
            backgroundColor: actionBackgroundColor,
          }}
        >
          {actions}
        </Box>
        <Box className={classes.header}>{title}</Box>
        <Box className={classes.kTemplates}>{kTemplates}</Box>
      </StyledMediaDetailFixedLayout>
    );
  };

export default MediaDetailFixedLayout;
